import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import UserContext from '../../../../../Contexts/UserContext/UserContext';
import APIWrapperContext from '../../../../../services/APIWrapperContext';
import { UserService } from '../../../../../services/openapi/services/UserService';
import { TaskDocument } from '../../../../../services/openapi/models/TaskDocument';
import { TaskWithImages } from '../hooks/useTaskDetail';

interface EmailDocumentModalProps {
  open: boolean;
  onClose: () => void;
  document: TaskDocument | null;
  task: TaskWithImages | null;
  onSend: (recipients: string[], subject: string, message: string) => Promise<void>;
}

export const EmailDocumentModal: React.FC<EmailDocumentModalProps> = ({
  open,
  onClose,
  document,
  task,
  onSend,
}) => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [currentEmail, setCurrentEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [currentUserFullName, setCurrentUserFullName] = useState<string>('');

  const { name: contextUserName, email: userEmail, additionalUserdata } = useContext(UserContext);
  const { call_backend_api } = useContext(APIWrapperContext);
  const sessionContext = useSessionContext();

  useEffect(() => {
    if (open) {
      
      if (contextUserName && contextUserName.trim()) {
        setCurrentUserFullName(contextUserName);
        return;
      }

      if (additionalUserdata?.fullname && additionalUserdata.fullname.trim()) {
        setCurrentUserFullName(additionalUserdata.fullname);
        return;
      }

      call_backend_api(
        UserService.readUsersMeUserMeGet(),
        (userInfo: any) => {

          
          let displayName = 'Boehringer Team';
          
          if (userInfo.fullname && userInfo.fullname.trim() && userInfo.fullname.trim() !== '') {
            displayName = userInfo.fullname.trim();
          } else if (userInfo.email && userInfo.email.trim() && userInfo.email.trim() !== '') {
            const emailName = userInfo.email.split('@')[0];
            displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          }
          
          setCurrentUserFullName(displayName);
        },
        (error: any) => {
          console.error('Error loading user info:', error);
          setCurrentUserFullName('Boehringer Team');
        },
        undefined,
        false
      );
    }
  }, [open, contextUserName, userEmail, additionalUserdata, call_backend_api]);

  React.useEffect(() => {
    if (open && document && task && currentUserFullName) {
      const defaultSubject = `Dokument aus Aufgabe: ${task.title}`;
      const senderName = currentUserFullName;
      const defaultMessage = `Hallo,

anbei finden Sie das Dokument "${document.filename}" aus der Aufgabe "${task.title}".

Mit freundlichen Grüßen
${senderName}`;

      setSubject(defaultSubject);
      setMessage(defaultMessage);
      setRecipients([]);
      setCurrentEmail('');
      setErrors({});
      setSending(false);
    }
  }, [open, document, task, currentUserFullName]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addRecipient = () => {
    const email = currentEmail.trim();
    if (!email) return;

    if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: 'Ungültige E-Mail-Adresse' }));
      return;
    }

    if (recipients.includes(email)) {
      setErrors(prev => ({ ...prev, email: 'E-Mail-Adresse bereits hinzugefügt' }));
      return;
    }

    setRecipients(prev => [...prev, email]);
    setCurrentEmail('');
    setErrors(prev => ({ ...prev, email: '' }));
  };

  const removeRecipient = (emailToRemove: string) => {
    setRecipients(prev => prev.filter(email => email !== emailToRemove));
  };

  const handleEmailKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addRecipient();
    }
  };

  const handleSend = async () => {
    const newErrors: {[key: string]: string} = {};
    
    if (recipients.length === 0) {
      newErrors.recipients = 'Mindestens ein Empfänger ist erforderlich';
    }
    
    if (!subject.trim()) {
      newErrors.subject = 'Betreff ist erforderlich';
    }
    
    if (!message.trim()) {
      newErrors.message = 'Nachricht ist erforderlich';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSending(true);
    try {
      await onSend(recipients, subject.trim(), message.trim());
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
    } finally {
      setSending(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={!sending ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Dokument per E-Mail versenden
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose} 
          disabled={sending}
          sx={{ 
            color: 'grey.500',
            '&:hover': { backgroundColor: 'grey.100' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Dokument-Information */}
        {document && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            backgroundColor: 'grey.50', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AttachFileIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>
                Anhang
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Datei:</strong> {document.filename}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Größe:</strong> {document.file_size ? formatFileSize(document.file_size) : 'Unbekannt'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Typ:</strong> {document.mime_type || 'Unbekannt'}
            </Typography>
          </Box>
        )}

        {/* Empfänger */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Empfänger
          </Typography>
          
          {/* Empfänger-Liste */}
          {recipients.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {recipients.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  onDelete={() => removeRecipient(email)}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          )}
          
          {/* E-Mail-Eingabe */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="E-Mail-Adresse eingeben"
              value={currentEmail}
              onChange={(e) => {
                setCurrentEmail(e.target.value);
                setErrors(prev => ({ ...prev, email: '' }));
              }}
              onKeyPress={handleEmailKeyPress}
              error={!!errors.email}
              helperText={errors.email}
              disabled={sending}
            />
            <Button
              variant="outlined"
              onClick={addRecipient}
              disabled={!currentEmail.trim() || sending}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              Hinzufügen
            </Button>
          </Box>
          
          {errors.recipients && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {errors.recipients}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Betreff */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Betreff"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setErrors(prev => ({ ...prev, subject: '' }));
            }}
            error={!!errors.subject}
            helperText={errors.subject}
            disabled={sending}
            size="small"
          />
        </Box>

        {/* Nachricht */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Nachricht"
            multiline
            rows={8}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setErrors(prev => ({ ...prev, message: '' }));
            }}
            error={!!errors.message}
            helperText={errors.message}
            disabled={sending}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              }
            }}
          />
        </Box>

        {/* Info */}
        <Alert severity="info" sx={{ mt: 2 }}>
          Das Dokument wird automatisch als Anhang zur E-Mail hinzugefügt.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        py: 2, 
        borderTop: '1px solid', 
        borderColor: 'divider',
        gap: 1 
      }}>
        <Button 
          onClick={onClose} 
          disabled={sending}
          color="inherit"
        >
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || recipients.length === 0}
          startIcon={sending ? <CircularProgress size={18} /> : <SendIcon />}
          sx={{
            minWidth: 120,
            fontWeight: 600,
          }}
        >
          {sending ? 'Sende...' : 'E-Mail senden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
